import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import api, { COMPANY_STYLES } from "@/api";
import { useAuth } from "@/AuthContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Plus, TriangleAlert } from "lucide-react";

export default function Stock() {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: "", company: "Cleanmates", unit: "units", stock_level: 0, reorder_threshold: 5 });
  const canEdit = ["super_admin", "admin", "accountant"].includes(user.role);

  const load = useCallback(() => {
    api.get("/stock").then((r) => setItems(r.data));
    api.get("/companies").then((r) => setCompanies(r.data));
  }, []);
  useEffect(() => { load(); }, [load]);

  const add = async (e) => {
    e.preventDefault();
    try {
      await api.post("/stock", { ...form, stock_level: parseFloat(form.stock_level), reorder_threshold: parseFloat(form.reorder_threshold) });
      toast.success("Item added"); setOpen(false); load();
    } catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
  };

  const restock = async (item) => {
    const qty = window.prompt(`Add stock to "${item.name}" (current ${item.stock_level} ${item.unit}):`, "10");
    if (!qty || isNaN(parseFloat(qty))) return;
    await api.patch(`/stock/${item.id}`, { stock_level: item.stock_level + parseFloat(qty) });
    toast.success("Stock updated"); load();
  };

  return (
    <div className="space-y-5" data-testid="stock-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="font-heading text-2xl font-bold tracking-tight">Consumables & Stock</h1>
          <p className="text-sm text-muted-foreground">Usage logged per job from the field app · auto reorder alerts</p>
        </div>
        {canEdit && <Button data-testid="add-stock-button" onClick={() => setOpen(true)} className="bg-primary hover:bg-primary-hover"><Plus size={16} className="mr-2" /> Add Item</Button>}
      </div>

      <Card className="overflow-hidden">
        <table className="w-full text-sm" data-testid="stock-table">
          <thead className="bg-muted/60">
            <tr className="text-left text-xs uppercase tracking-widest text-muted-foreground">
              <th className="px-4 py-3">Item</th><th className="px-4 py-3">Brand</th><th className="px-4 py-3 text-right">Stock</th>
              <th className="px-4 py-3 text-right">Reorder At</th><th className="px-4 py-3">Health</th>{canEdit && <th className="px-4 py-3 text-right">Actions</th>}
            </tr>
          </thead>
          <tbody>
            {items.map((i) => {
              const low = i.stock_level <= i.reorder_threshold;
              return (
                <tr key={i.id} data-testid={`stock-row-${i.id}`} className="border-t hover:bg-accent/40 transition-colors">
                  <td className="px-4 py-3 font-medium">{i.name}</td>
                  <td className="px-4 py-3"><span className={`text-xs px-2 py-1 rounded-full border ${COMPANY_STYLES[i.company] || ""}`}>{i.company}</span></td>
                  <td className="px-4 py-3 text-right font-mono">{i.stock_level} {i.unit}</td>
                  <td className="px-4 py-3 text-right font-mono text-muted-foreground">{i.reorder_threshold}</td>
                  <td className="px-4 py-3">
                    {low
                      ? <span className="text-xs px-2 py-1 rounded-full border bg-danger text-danger-foreground border-danger inline-flex items-center gap-1" data-testid={`stock-low-${i.id}`}><TriangleAlert size={11} /> Reorder</span>
                      : <span className="text-xs px-2 py-1 rounded-full border bg-success-tint text-success border-success/30">Healthy</span>}
                  </td>
                  {canEdit && <td className="px-4 py-3 text-right"><Button size="sm" variant="outline" data-testid={`restock-${i.id}`} onClick={() => restock(i)} className="h-8">Restock</Button></td>}
                </tr>
              );
            })}
            {items.length === 0 && <tr><td colSpan={6} className="px-4 py-10 text-center text-muted-foreground">No items</td></tr>}
          </tbody>
        </table>
      </Card>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="add-stock-dialog">
          <DialogHeader><DialogTitle className="font-heading">New Stock Item</DialogTitle></DialogHeader>
          <form onSubmit={add} className="space-y-3">
            <Input data-testid="stock-name-input" placeholder="Item name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required />
            <Select value={form.company} onValueChange={(v) => setForm({ ...form, company: v })}>
              <SelectTrigger data-testid="stock-company-select"><SelectValue /></SelectTrigger>
              <SelectContent>{companies.map((c) => <SelectItem key={c.code} value={c.name}>{c.name}</SelectItem>)}</SelectContent>
            </Select>
            <div className="grid grid-cols-3 gap-3">
              <Input data-testid="stock-unit-input" placeholder="Unit" value={form.unit} onChange={(e) => setForm({ ...form, unit: e.target.value })} />
              <Input data-testid="stock-level-input" type="number" min="0" placeholder="Stock" value={form.stock_level} onChange={(e) => setForm({ ...form, stock_level: e.target.value })} />
              <Input data-testid="stock-threshold-input" type="number" min="0" placeholder="Reorder at" value={form.reorder_threshold} onChange={(e) => setForm({ ...form, reorder_threshold: e.target.value })} />
            </div>
            <Button data-testid="stock-save-button" type="submit" className="w-full bg-primary hover:bg-primary-hover">Save</Button>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
